import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useWorkListActions, useWorkListState } from '../../../shared/hooks/useApi/useWorkList';
import { useWorkViewStore } from '../../../shared/store/WorkViewStore';
import { WorkData } from '../../../shared/dto/EntityRepository';
import { useBulkStore, useWorkListStore } from '../../../shared/store/WorkListStore';
import EmptyWorkList from '../EmptyWorkList';
import WorkInfoMobile from './WorkInfoMobile';
import AttributedWorkInfoMobile from './AttributedWorkInfoMobile';
import WorkViewerMobile from './WorkViewerMobile';
import { useUnsavedChanges } from '../../../shared/hooks/useUnsavedChanges';
import { useEditMode } from '../../../shared/hooks/useEditMode';
import createIcon from '../../../asset/icon/create.png';
import { useAuth } from '../../../shared/hooks/useApi/useAuth';
import { useBulk } from '../../../shared/hooks/useApi/useBulk';
import { useModalStore } from '../../../shared/store/portal/modalStore';
import { useStandardAlertStore } from '../../../shared/store/portal/alertStore';
import { getAlertMessage } from '../../../shared/constants/alertMessages';
import { BULK_UPLOAD_IMAGE_MAX_COUNT } from '../../../shared/constants/limits';
import { AlertPosition, AlertType, ModalType } from '../../../shared/enum/EnumRepository';
import { getImageDimensions, removeFileExtension } from '../../../shared/util/imageUtils';
import { filterSupportedImageFiles } from '../../../shared/util/imageFileFormat';
import { UpdateImgReq } from '../../../shared/dto/ReqDtoRepository';
import { useWorkTagStore } from '../../../shared/store/workTagStore';
import { withWorkLabelFilter } from '../buildWorkListParams';
import UniversalIconBtn from '../../../shared/component/headless/button/UniversalIconBtn';
import { bottomWithSafeArea } from '../../../shared/constants/safeArea';
import { usePageOwnerIdentityStore } from '../../../shared/store/pageOwnerIdentityStore';
import { useParams } from 'react-router-dom';
import { isPageOwnerMatchedToRaw } from '../../../shared/util/pageOwnerMatch';
import { useSettingStore } from '../../../shared/store/settingStore';
import { shouldShowWorkArtist } from '../../../shared/util/workArtist';
import { canShowWorkList } from '../../../shared/util/visibility/workListVisibility';
import type { ResourceScope } from '../../../shared/util/scopedResource';
import WorkImportButton from '../WorkImportButton';

const CREATE_WORK_INPUT_ID = 'works-create-work-input-mobile';

const WorkListMobile: React.FC = () => {
  const setting = useSettingStore((state) => state.setting);
  const WorkInfoRow = shouldShowWorkArtist(setting) ? AttributedWorkInfoMobile : WorkInfoMobile;
  const { isEditMode } = useEditMode();
  const { isOwner } = useAuth();
  const { workList, listOwnerAui, listScope, status } = useWorkListState();
  const {
    getWorkList,
    getWorkListOwner,
    getWork,
    getWorkOwner,
    updateWorkBulk,
    clearActiveWork,
  } = useWorkListActions();
  const { createWorkBulk } = useBulk();
  const pageSize = useWorkListStore((s) => s.pageSize);
  const sortData = useWorkListStore((s) => s.sortData);
  const filterData = useWorkListStore((s) => s.filterData);
  const activeWork = useWorkViewStore((s) => s.activeWork);
  const setActiveWork = useWorkViewStore((s) => s.setActiveWork);
  const [selectedId, setSelectedId] = useState<string>("");
  const { runAfterUnsavedChangesCheck } = useUnsavedChanges();
  const { setStandardModal } = useModalStore();
  const { setTotal, clearCurrent } = useBulkStore();
  const { enqueueAlert } = useStandardAlertStore();
  const selectedTags = useWorkTagStore((s) => s.selectedTags);
  const untaggedSelected = useWorkTagStore((s) => s.untaggedSelected);
  const pageOwner = usePageOwnerIdentityStore(s => s.pageOwner);
  const { HANDLE: raw } = useParams<{ HANDLE?: string }>();
  const currentScope: ResourceScope = isOwner ? 'owner' : 'public';
  const canShowList = canShowWorkList({
    pageOwnerAui: pageOwner?.aui,
    listOwnerAui,
    listScope,
    currentScope,
    status,
  });
  const visibleWorkList = canShowList ? workList : [];

  useEffect(() => {
    if (!activeWork) {
      setSelectedId("");
    }
  }, [activeWork]);

  useEffect(() => {
    const getWorkListWithApi = async () => {
      if (!isPageOwnerMatchedToRaw(raw, pageOwner)) return;
      if (isOwner) {
        await getWorkListOwner({ page: 1, size: pageSize, sortData: sortData, filterData: withWorkLabelFilter(filterData, selectedTags, untaggedSelected, isOwner) });
      } else {
        await getWorkList({ page: 1, size: pageSize, sortData: sortData, filterData: withWorkLabelFilter(filterData, selectedTags, untaggedSelected, isOwner) });
      }
    }
    getWorkListWithApi();
    setActiveWork(null);
  }, [raw, pageOwner?.aui, pageSize, sortData, filterData, isOwner, selectedTags, untaggedSelected]);

  const createSingleWorkFromFile = async (file: File) => {
    setTotal(1);
    clearCurrent();
    const results = await createWorkBulk([file]);
    if (!results?.length) return;
    const { width, height } = await getImageDimensions(file);
    const workListPayload: UpdateImgReq[] = [{
      id: results[0].workId,
      originUrl: results[0].originUrl,
      width,
      height,
      title: removeFileExtension(file.name),
    }];
    await updateWorkBulk({ workList: workListPayload });
    await getWorkListOwner({ page: 1, size: pageSize, sortData, filterData: withWorkLabelFilter(filterData, selectedTags, untaggedSelected, isOwner) });
  };

  const handleCreateWork = () => {
    document.getElementById(CREATE_WORK_INPUT_ID)?.click();
  };

  const handleCreateWorkFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = filterSupportedImageFiles(Array.from(e.target.files ?? []));
    e.target.value = '';
    if (files.length === 0) return;

    if (files.length > BULK_UPLOAD_IMAGE_MAX_COUNT) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('BULK_UPLOAD_IMAGE_MAX_EXCEEDED', { maxCount: BULK_UPLOAD_IMAGE_MAX_COUNT })
      });
      return;
    }

    if (files.length === 1) {
      const [file] = files;
      void createSingleWorkFromFile(file);
      return;
    }

    setTotal(files.length);
    clearCurrent();
    setStandardModal({
      modalType: ModalType.BULK_UPLOAD,
      title: 'Works',
      value: 'list',
      data: files,
      handleChange: () => { }
    });
  };

  const handleClick = (selectedId: string) => {
    changeActiveWork(selectedId);
  };
  
  /**
   * 새로운 newId를 받아서  
   * ActiveWork를 변경한다.
   * - newId가 변경되었으면 변경한다.
   */
  const changeActiveWork = (newId: string) => {
    const getWorkWithDetailWithApi = async () => {
      console.log("getting work and Detail List...")
      clearActiveWork();
      if (isOwner) {
        await getWorkOwner(newId);
      } else {
        await getWork(newId);
      }
      setSelectedId(newId);
    }
    if (newId !== selectedId) {
      runAfterUnsavedChangesCheck(() => {
        getWorkWithDetailWithApi();
      });
    }
  }

  return (
    <WorkListContainer>
      <WorkListComp $isActive={!!activeWork}>
        {visibleWorkList.map((each: WorkData) =>
          <WorkInfoRow
            key={each.id}
            data={each}
            handleClick={() => handleClick(each.id)}
          />
        )}
        {canShowList && visibleWorkList.length === 0 && <EmptyWorkList />}
        {isEditMode &&
          <BtnContainer>
            <WorkImportButton />
            <HiddenFileInput
              id={CREATE_WORK_INPUT_ID}
              type="file"
              accept="image/*"
              multiple
              onChange={handleCreateWorkFileChange}
            />
            <UniversalIconBtn 
              size="standard" 
              border={true} 
              backgroundImg={createIcon} 
              onClick={handleCreateWork} />
          </BtnContainer>
        }
      </WorkListComp>
      <WorkViewerMobile />
    </WorkListContainer>
  );
}
const WorkListContainer = styled.div`
  width: 100%;
  flex: 1;
  min-height: 0;
  display: flex;
  outline: none;
`;

const WorkListComp = styled.section<{ $isActive: boolean }>`
  width: ${({ $isActive }) => $isActive ? '0%' : '100%'};
  opacity: ${({ $isActive }) => $isActive ? 0 : 1};
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  border-bottom: 1px solid ${({ theme }) => theme.colors.color_Gray_04};
  transition: all 0.7s ease-in-out;
  
  &::-webkit-scrollbar {
    display: none;
  }
`;

const BtnContainer = styled.div`
  position: fixed;
  bottom: ${bottomWithSafeArea('clamp(30px, 7vw, 40px)')};
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  justify-content: center;
  gap: 0.4vw;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

export default WorkListMobile;
